import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { api } from "../api";
import { CopyButton } from "../components/CopyButton";
import { Field, isPending, PendingBadge, safeHref } from "../components/Field";
import { StatusBadge, VerificationBadge, type PublicVerification } from "../components/StatusBadge";

type Dept = { department: Record<string, unknown> & { name: string; acronym: string | null; level: string; state_code: string | null;
  contacts: { id: string; label: string; value: string; hours: string | null }[] | { pending: true };
  faqs: { id: string; question: string; answer: string }[] | { pending: true }; verification: PublicVerification };
  parent: { slug: string; name: string } | null;
  programs: { id: string; slug: string; name: string; status: string; verification: PublicVerification }[] };

export function DepartmentDetail() {
  const { slug } = useParams({ from: "/app/directory/departments/$slug" });
  const q = useQuery({ queryKey: ["department", slug], queryFn: () => api<Dept>(`/api/directory/departments/${encodeURIComponent(slug)}`) });
  if (q.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (q.error) return <p role="alert" className="text-sm text-red-600">Could not load this department.</p>;
  const { department: d, parent, programs } = q.data!;
  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold">{d.name}{d.acronym && ` (${d.acronym})`}</h1>
        <div className="text-xs text-slate-500">{d.level}{d.state_code && ` · ${d.state_code}`}{parent && <> · part of {parent.name}</>}</div>
        <VerificationBadge v={d.verification} />
      </div>
      <section className="card grid gap-4 sm:grid-cols-2">
        <Field label="About" value={d.description} />
        <div><div className="label">Website</div>{isPending(d.website_url) ? <PendingBadge />
          : safeHref(d.website_url) ? <a className="text-sm text-brand-700 hover:underline" href={String(d.website_url)} target="_blank" rel="noreferrer noopener">{String(d.website_url)}</a>
          : d.website_url ? <span className="text-sm">{String(d.website_url)}</span>
          : <span className="text-sm text-slate-400">Not listed</span>}</div>
      </section>
      <section className="card"><h2 className="mb-2 text-sm font-semibold">Contacts</h2>
        {isPending(d.contacts) ? <PendingBadge /> : d.contacts.length === 0 ? <p className="text-sm text-slate-400">Not listed</p> :
          <ul className="flex flex-col gap-1.5">{d.contacts.map((c) => (
            <li key={c.id} className="flex items-center justify-between text-sm"><span><span className="text-slate-500">{c.label}: </span><b>{c.value}</b>{c.hours && ` · ${c.hours}`}</span><CopyButton text={c.value} /></li>
          ))}</ul>}
      </section>
      {!isPending(d.faqs) && d.faqs.length > 0 && (
        <section className="card flex flex-col gap-2"><h2 className="text-sm font-semibold">Common questions</h2>
          {d.faqs.map((f) => <details key={f.id} className="rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800"><summary className="cursor-pointer text-sm font-medium">{f.question}</summary><p className="mt-1 text-sm">{f.answer}</p></details>)}
        </section>
      )}
      <section className="card"><h2 className="mb-2 text-sm font-semibold">Programs</h2>
        {programs.length === 0 ? <p className="text-sm text-slate-400">None in the directory yet.</p> :
          <ul className="flex flex-col gap-1">{programs.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2 text-sm">
              <Link to="/directory/programs/$slug" params={{ slug: p.slug }} search={{}} className="text-brand-700 hover:underline">{p.name}</Link>
              <span className="flex gap-2"><StatusBadge status={p.status} /><VerificationBadge v={p.verification} /></span>
            </li>
          ))}</ul>}
      </section>
    </div>
  );
}
